import { stringSimilarity } from '@partsbadger/utils/lib/files/string-similarity';

/** Pairs scoring at least this are matched first, best pair first. */
export const CONFIDENT_MATCH = 0.5;
/** Weaker pairs are matched only when each file is the other's best remaining choice. */
export const WEAK_MATCH = 0.2;

// Tokens that name the kind of file rather than the part ("Bracket Drawing.pdf").
const NOISE_TOKENS = new Set(['drawing', 'drawings', 'dwg', 'drw', 'print', 'copy', 'final']);

// On equal scores, prefer the formats the shop quotes from (STEP over IGES, PDF over DXF).
const PREFERRED_3D = [
    'step',
    'stp',
    'sldprt',
    'x_t',
    'x_b',
    'prt',
    'ipt',
    'catpart',
    'iges',
    'igs',
    'stl',
    'obj',
    '3mf',
];
const PREFERRED_2D = ['pdf', 'slddrw', 'dwg', 'dxf', 'tif', 'png', 'jpg', 'jpeg'];

const extension = (filename: string) => filename.split('.').pop()?.toLowerCase() ?? '';

const formatRank = (preferred: Array<string>, filename: string) => {
    const index = preferred.indexOf(extension(filename));
    return index < 0 ? preferred.length : index;
};

/** Lower-cased alphanumeric tokens of the name, without the extension or a "(1)" duplicate marker. */
export const nameTokens = (filename: string): Array<string> =>
    filename
        .slice(filename.lastIndexOf('/') + 1)
        .split('.')
        .slice(0, -1)
        .join('.')
        .toLowerCase()
        .replace(/\s*\(\d{1,2}\)\s*$/, '')
        .split(/[^0-9a-z]+/)
        .filter(Boolean);

/** Tokens that identify the part: drops "drawing"-like words and "Rev X" markers. */
const partTokens = (filename: string): Array<string> => {
    const tokens = nameTokens(filename);
    const kept: Array<string> = [];
    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i] === 'rev' || tokens[i] === 'revision') {
            if (tokens[i + 1] && tokens[i + 1].length <= 3) i++;
            continue;
        }
        if (!NOISE_TOKENS.has(tokens[i])) kept.push(tokens[i]);
    }
    return kept;
};

const sameToken = (a: string, b: string) => a === b || (/^\d+$/.test(a) && /^\d+$/.test(b) && Number(a) === Number(b));

/**
 * How likely a 3D file and a 2D file belong to the same part, from 0 to 1.
 * Same name → 1; same part name once "drawing"/"Rev" words are ignored → 0.99;
 * otherwise bigram similarity, raised to CONFIDENT_MATCH when every token of the
 * shorter name appears in the longer one ("253.pdf" and "ARM, LIFTING (253).STEP").
 */
export const matchScore = (file3d: string, file2d: string): number => {
    const tokens3d = nameTokens(file3d);
    const tokens2d = nameTokens(file2d);
    const full3d = tokens3d.join('');
    const full2d = tokens2d.join('');
    if (full3d && full3d === full2d) return 1;

    const part3d = partTokens(file3d);
    const part2d = partTokens(file2d);
    if (part3d.length && part3d.join('') === part2d.join('')) return 0.99;

    const similarity = Math.max(stringSimilarity(full3d, full2d), stringSimilarity(part3d.join(''), part2d.join('')));
    const [short, long] = part3d.length <= part2d.length ? [part3d, part2d] : [part2d, part3d];
    const distinctive = short.length >= 2 || (short.length === 1 && short[0].length >= 3 && /\d/.test(short[0]));
    if (distinctive && short.every(token => long.some(other => sameToken(token, other)))) {
        return Math.max(similarity, CONFIDENT_MATCH);
    }
    return similarity;
};

export interface MatchedPair<T> {
    file3d: T;
    file2d: T;
    score: number;
}

/**
 * Pair 3D files with 2D files by name. The result does not depend on the order of the
 * files: confident pairs are taken best first, then weak pairs only between mutual best
 * choices, and when exactly one 3D and one 2D file are left they are paired if their names
 * have something in common.
 */
export const matchPartFiles = <T>(
    files3d: Array<T>,
    files2d: Array<T>,
    nameOf: (file: T) => string
): Array<MatchedPair<T>> => {
    const names3d = files3d.map(nameOf);
    const names2d = files2d.map(nameOf);
    const scores = names3d.map(name3d => names2d.map(name2d => matchScore(name3d, name2d)));

    type Candidate = { i: number; j: number; score: number };
    const compare = (a: Candidate, b: Candidate) =>
        b.score - a.score ||
        formatRank(PREFERRED_3D, names3d[a.i]) - formatRank(PREFERRED_3D, names3d[b.i]) ||
        formatRank(PREFERRED_2D, names2d[a.j]) - formatRank(PREFERRED_2D, names2d[b.j]) ||
        names3d[a.i].localeCompare(names3d[b.i]) ||
        names2d[a.j].localeCompare(names2d[b.j]) ||
        a.i - b.i ||
        a.j - b.j;

    const used3d = new Set<number>();
    const used2d = new Set<number>();
    const pairs: Array<MatchedPair<T>> = [];
    const take = ({ i, j, score }: Candidate) => {
        used3d.add(i);
        used2d.add(j);
        pairs.push({ file3d: files3d[i], file2d: files2d[j], score });
    };
    const candidates = (minScore: number) => {
        const found: Array<Candidate> = [];
        files3d.forEach((_, i) => {
            if (used3d.has(i)) return;
            files2d.forEach((__, j) => {
                if (!used2d.has(j) && scores[i][j] >= minScore) found.push({ i, j, score: scores[i][j] });
            });
        });
        return found.sort(compare);
    };

    candidates(CONFIDENT_MATCH).forEach(candidate => {
        if (!used3d.has(candidate.i) && !used2d.has(candidate.j)) take(candidate);
    });

    for (;;) {
        const weak = candidates(WEAK_MATCH);
        const mutual = weak.find(candidate =>
            weak.every(
                other =>
                    other === candidate ||
                    (other.i !== candidate.i && other.j !== candidate.j) ||
                    compare(candidate, other) < 0
            )
        );
        if (!mutual) break;
        take(mutual);
    }

    const left3d = files3d.map((_, i) => i).filter(i => !used3d.has(i));
    const left2d = files2d.map((_, j) => j).filter(j => !used2d.has(j));
    // The last 3D file and the last drawing are paired only when their names have something
    // in common (master's rule): two unrelated files are two parts.
    if (left3d.length === 1 && left2d.length === 1 && scores[left3d[0]][left2d[0]] > 0) {
        take({ i: left3d[0], j: left2d[0], score: scores[left3d[0]][left2d[0]] });
    }

    return pairs;
};
